import { useState } from 'react'
import { useGameStore } from '@/store/gameStore'
import { GAME_CONFIG } from '@/config/gameConfig'
import { useFeatureUnlocked } from '@/ui/components/FeatureGate'
import { FEATURES, CLUB_LEVELS } from '@/config/clubLevelConfig'
import { SectionDeck, IconTile, ConfirmBar, EmptyStateRich } from '../components/CourtVision'
import { FACILITY_VISUALS } from '@/config/campusConfig'
import {
  courtLicenseGate, getDivisionOrNone, meetsDivision, divisionGateLabel, divisionGateMessage,
} from '@/config/federationConfig'
import { courtConstructionDays, daysRemaining } from '@/config/constructionConfig'
import { EventBus, GameEvents } from '@/game/utils/EventBus'
import { LandPlot, Warehouse, Lock, Hammer, Plus, HardHat } from 'lucide-react'

const COURT_VISUAL = FACILITY_VISUALS.find(v => v.id === 'court')
const courtImg = (tier: number) => COURT_VISUAL?.tiers.find(t => t.tier === tier)?.image

/** Small court preview thumbnail; falls back to a gradient if the asset is missing */
function CourtThumb({ image, gradient }: { image?: string; gradient: string }) {
  return (
    <span
      style={{
        width: 56, height: 42, flexShrink: 0, borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--glass-border)',
        backgroundImage: image ? `url(${image})` : undefined,
        backgroundColor: gradient,
        backgroundSize: 'cover', backgroundPosition: 'center',
      }}
    />
  )
}

export function BuildTabContent() {
  const [confirming, setConfirming] = useState<'outdoor' | 'indoor' | null>(null)
  const { money, courts, queueConstruction, constructionQueue, day, league } = useGameStore()

  // Licencia Federativa: the next court may need a minimum league division
  const currentDivision = getDivisionOrNone(league)
  const queuedCourts = constructionQueue.filter(j => j.kind === 'court')
  const totalWithQueued = courts.length + queuedCourts.length
  const indoorWithQueued = courts.filter(c => c.type === 'indoor').length
    + queuedCourts.filter(j => j.courtType === 'indoor').length
  const gateFor = (type: 'outdoor' | 'indoor') => {
    const gate = courtLicenseGate(totalWithQueued, indoorWithQueued, type)
    return gate !== null && !meetsDivision(currentDivision, gate) ? gate : null
  }

  const indoorUnlocked = useFeatureUnlocked(FEATURES.BUILD_INDOOR)
  const indoorLevel = CLUB_LEVELS.find(l => l.unlocks.includes(FEATURES.BUILD_INDOOR))

  const outdoorCost = GAME_CONFIG.COURT_OUTDOOR_COST
  const indoorCost = GAME_CONFIG.COURT_INDOOR_COST

  const pendingCourts = constructionQueue.filter(j => j.kind === 'court')

  const buildCourt = (type: 'outdoor' | 'indoor') => {
    const cost = type === 'outdoor' ? outdoorCost : indoorCost
    const label = type === 'outdoor' ? 'Pista Outdoor' : 'Pista Indoor'
    const days = courtConstructionDays(type)
    const ok = queueConstruction({
      kind: 'court',
      label,
      totalDays: days,
      visualId: 'court',
      tier: type === 'outdoor' ? 0 : 3,
      courtType: type,
      pricePerHour: type === 'outdoor' ? 15 : 25,
      cost,
    })
    EventBus.emit(GameEvents.NOTIFY, ok
      ? { message: `Obra iniciada: ${label} · ${days} días`, type: 'success' }
      : { message: 'No se pudo iniciar la obra (fondos insuficientes)', type: 'error' })
    setConfirming(null)
  }

  const outdoorCount = courts.filter(c => c.type === 'outdoor').length
  const indoorCount = courts.filter(c => c.type === 'indoor').length
  const outdoorDays = courtConstructionDays('outdoor')
  const indoorDays = courtConstructionDays('indoor')

  return (
    <div className="space-y-5">
      {courts.length === 0 && pendingCourts.length === 0 && (
        <EmptyStateRich
          icon={Hammer}
          title="El solar espera tu primera pista"
          sub="Una pista outdoor cuesta 5.000€ y, tras unos días de obra, empieza a generar ingresos"
        />
      )}

      {pendingCourts.length > 0 && (
        <SectionDeck title="En obras" icon={HardHat} meta={`${pendingCourts.length} en curso`}>
          <div className="space-y-2">
            {pendingCourts.map(job => {
              const r = daysRemaining(job, day)
              return (
                <div key={job.id} className="row-tile">
                  <IconTile icon={HardHat} tone="warning" />
                  <div className="row-tile-main">
                    <div className="row-tile-title">{job.label}</div>
                    <div className="row-tile-sub">Obra en curso · {job.totalDays} días</div>
                  </div>
                  <span className="chip-angled tone-gold">{r === 0 ? 'Listo hoy' : `${r}d`}</span>
                </div>
              )
            })}
          </div>
        </SectionDeck>
      )}

      <SectionDeck title="Construir pistas" icon={Plus} meta={`${outdoorCount} outdoor · ${indoorCount} indoor`}>
        <div className="space-y-2">
          {gateFor('outdoor') !== null ? (
            <div className="row-tile" style={{ opacity: 0.55 }} title={divisionGateMessage(gateFor('outdoor')!)}>
              <CourtThumb image={courtImg(0)} gradient="#1E3A5F" />
              <div className="row-tile-main">
                <div className="row-tile-title">Pista Outdoor</div>
                <div className="row-tile-sub">La Federación limita la ampliación: asciende en la liga</div>
              </div>
              <span className="pill pill-warning"><Lock size={10} /> {divisionGateLabel(gateFor('outdoor')!)}</span>
            </div>
          ) : confirming === 'outdoor' ? (
            <ConfirmBar
              icon={LandPlot}
              message={<>Construir <strong>Pista Outdoor</strong> por <strong className="money-positive">{outdoorCost.toLocaleString('es-ES')}€</strong> · <strong>{outdoorDays} días</strong> de obra</>}
              confirmLabel="Iniciar obra"
              onConfirm={() => buildCourt('outdoor')}
              onCancel={() => setConfirming(null)}
            />
          ) : (
            <button
              className="row-tile"
              onClick={() => setConfirming('outdoor')}
              disabled={money < outdoorCost}
            >
              <CourtThumb image={courtImg(0)} gradient="#1E3A5F" />
              <div className="row-tile-main">
                <div className="row-tile-title">Pista Outdoor</div>
                <div className="row-tile-sub">Al aire libre · {outdoorDays} días de obra · mantenimiento 50€/sem</div>
              </div>
              <div className="row-tile-side">
                <span className={money >= outdoorCost ? 'money-positive' : 'money-negative'} style={{ fontWeight: 700 }}>
                  {outdoorCost.toLocaleString('es-ES')}€
                </span>
                {outdoorCount > 0 && <span className="pill">{outdoorCount} construidas</span>}
              </div>
            </button>
          )}

          {indoorUnlocked && gateFor('indoor') !== null ? (
            <div className="row-tile" style={{ opacity: 0.55 }} title={divisionGateMessage(gateFor('indoor')!)}>
              <CourtThumb image={courtImg(3)} gradient="#1E3A5F" />
              <div className="row-tile-main">
                <div className="row-tile-title">Pista Indoor</div>
                <div className="row-tile-sub">La Federación limita la ampliación: asciende en la liga</div>
              </div>
              <span className="pill pill-warning"><Lock size={10} /> {divisionGateLabel(gateFor('indoor')!)}</span>
            </div>
          ) : indoorUnlocked ? (
            confirming === 'indoor' ? (
              <ConfirmBar
                icon={Warehouse}
                message={<>Construir <strong>Pista Indoor</strong> por <strong className="money-positive">{indoorCost.toLocaleString('es-ES')}€</strong> · <strong>{indoorDays} días</strong> de obra</>}
                confirmLabel="Iniciar obra"
                onConfirm={() => buildCourt('indoor')}
                onCancel={() => setConfirming(null)}
              />
            ) : (
              <button
                className="row-tile"
                onClick={() => setConfirming('indoor')}
                disabled={money < indoorCost}
              >
                <CourtThumb image={courtImg(3)} gradient="#1E3A5F" />
                <div className="row-tile-main">
                  <div className="row-tile-title">Pista Indoor</div>
                  <div className="row-tile-sub">Cubierta · {indoorDays} días de obra · mantenimiento 100€/sem</div>
                </div>
                <div className="row-tile-side">
                  <span className={money >= indoorCost ? 'money-positive' : 'money-negative'} style={{ fontWeight: 700 }}>
                    {indoorCost.toLocaleString('es-ES')}€
                  </span>
                  {indoorCount > 0 && <span className="pill">{indoorCount} construidas</span>}
                </div>
              </button>
            )
          ) : (
            <div className="row-tile" style={{ opacity: 0.55 }}>
              <IconTile icon={Lock} tone="muted" />
              <div className="row-tile-main">
                <div className="row-tile-title">Pista Indoor</div>
                <div className="row-tile-sub">Se desbloquea en Nv.{indoorLevel?.level}: {indoorLevel?.name}</div>
              </div>
              <span className="pill pill-warning"><Lock size={10} /> Bloqueada</span>
            </div>
          )}
        </div>
      </SectionDeck>
    </div>
  )
}
